import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Avatar,
  Alert,
  Button,
  Stack,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  Event,
  Star,
  VideoLibrary,
} from '@mui/icons-material';

import RevenueChart from './components/RevenueChart';
import OrdersChart from './components/OrdersChart';
import IntervenantsTable from './components/IntervenantsTable';
import AteliersRemplissage from './components/AteliersRemplissage';
import OffresRemplissage from './components/OffresRemplissage';
import PerformanceScoreCard from './components/PerformanceScoreCard'; // Importation de votre composant
import PlatformIntelligenceDashboard from './components/PlatformIntelligenceDashboard'; // Import du nouveau composant
import { useAuth } from '../../contexts/AuthContext';
import RankedClients from './components/RankedClients';
export default function Statistiques() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const rolePath = useMemo(() => {
    return user?.role === 1 || user?.role === 2 ? 'admin' : 'intervenant';
  }, [user]);
  // Fusion des offres de tous les intervenants pour l'affichage Global
  const toutesLesOffres = useMemo(() => {
    if (!data?.topIntervenants) return [];
    return data.topIntervenants.reduce((acc, intervenant) => {
      if (intervenant.offers && Array.isArray(intervenant.offers)) {
        const offresAvecNom = intervenant.offers.map((off) => ({
          ...off,
          intervenantName: `${intervenant.prenom} ${intervenant.nom}`,
        }));
        return [...acc, ...offresAvecNom];
      }
      return acc;
    }, []);
  }, [data]);

  const fetchStatistiques = async () => {
    if (!apiUrl) {
      setError("L'URL de l'API (REACT_APP_API_URL) n'est pas configurée.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${apiUrl}/${rolePath}/v1/statistiques/intervenant`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok)
        throw new Error('Erreur lors de la récupération des données');

      const result = await response.json();
      if (result.status === 'success' && result.dashboard) {
        setData(result.dashboard);
      } else {
        throw new Error('Format de réponse invalide');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatistiques();
  }, [apiUrl, rolePath, token]);

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="70vh"
      >
        <CircularProgress size={45} thickness={4} sx={{ color: '#3b82f6' }} />
      </Box>
    );

  if (error)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        p={3}
      >
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Button color="primary" size="small" onClick={fetchStatistiques}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );

  if (!data || !data.kpis)
    return (
      <Box p={4}>
        <Alert severity="warning" variant="outlined">
          Aucune donnée disponible.
        </Alert>
      </Box>
    );

  const { kpis } = data;

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        backgroundColor: '#f9fafb',
        minHeight: '100vh',
      }}
    >
      <Box mb={4}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827' }}>
          Tableau de Bord Analytique
        </Typography>
        <Typography variant="body1" sx={{ color: '#4b5563' }}>
          Suivi des performances •{' '}
          <Box component="span" sx={{ fontWeight: 600, color: '#3b82f6' }}>
            {rolePath === 'admin' ? 'Vue Administrateur' : 'Vue Intervenant'}
          </Box>
        </Typography>
      </Box>

      {/* KPIs */}
      {/* Section KPIs - Ligne Unique Maximisée & Équilibrée (7 KPIs) */}
      <Paper
        sx={{
          p: 2.5, // Augmentation du padding global de la carte pour faire respirer les bords
          borderRadius: 4,
          bgcolor: '#ffffff',
          border: '1px solid #f1f5f9',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.02)',
          mb: 4,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {' '}
          {/* Spacing augmenté pour créer de l'espace entre les blocs */}
          {/* 1. Chiffre d'Affaires Global */}
          <Grid item xs={12} sm={4} md={1.6}>
            <Box sx={{ px: 2 }}>
              {' '}
              {/* Padding horizontal augmenté à 2 pour aérer le contenu */}
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Chiffre d'Affaires
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#0f172a',
                  mt: 0.5,
                  letterSpacing: '-0.5px',
                }}
              >
                {(kpis.revenue || 0).toLocaleString('fr-FR')} €
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  mt: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Panier moy : <strong>{kpis.avg_order_value || 0}€</strong>
              </Typography>
            </Box>
          </Grid>
          <Grid item md={0.1} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 35, borderColor: '#e2e8f0' }}
            />
          </Grid>
          {/* 2. Taux de Conversion */}
          <Grid item xs={12} sm={4} md={1.8}>
            <Box sx={{ px: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Conversion Cdes
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#4f46e5',
                  mt: 0.5,
                  letterSpacing: '-0.5px',
                }}
              >
                {kpis.success_rate || 0}%
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#10b981',
                  mt: 0.5,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {kpis.successful_orders || 0} / {kpis.orders || 0} validées
              </Typography>
            </Box>
          </Grid>
          <Grid item md={0.1} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 35, borderColor: '#e2e8f0' }}
            />
          </Grid>
          {/* 3. Catalogue Actif */}
          <Grid item xs={12} sm={4} md={1.6}>
            <Box sx={{ px: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Catalogue Actif
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#0f172a',
                  mt: 0.5,
                  letterSpacing: '-0.5px',
                }}
              >
                {(kpis.workshops || 0) + (kpis.offers || 0)}{' '}
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#94a3b8',
                    fontWeight: 500,
                  }}
                >
                  prods
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  mt: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                {kpis.workshops || 0} atelier • {kpis.offers || 0} consultation
                • {kpis.videos || 0} vidéo
              </Typography>
            </Box>
          </Grid>
          <Grid item md={0.1} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 35, borderColor: '#e2e8f0' }}
            />
          </Grid>
          {/* 4. Intervenants Actifs */}
          <Grid item xs={12} sm={4} md={1.6}>
            <Box sx={{ px: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Intervenants
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#059669',
                  mt: 0.5,
                  letterSpacing: '-0.5px',
                }}
              >
                {kpis.active_intervenants || 0}{' '}
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#94a3b8',
                    fontWeight: 500,
                  }}
                >
                  / {kpis.total_intervenants || 0}
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  mt: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Actifs :{' '}
                <strong>
                  {(
                    (kpis.active_intervenants / kpis.total_intervenants) *
                    100
                  ).toFixed(0)}
                  %
                </strong>
              </Typography>
            </Box>
          </Grid>
          <Grid item md={0.1} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 35, borderColor: '#e2e8f0' }}
            />
          </Grid>
          {/* 5. Clients Uniques */}
          <Grid item xs={12} sm={4} md={1.6}>
            <Box sx={{ px: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Clients Uniques
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#db2777',
                  mt: 0.5,
                  letterSpacing: '-0.5px',
                }}
              >
                {kpis.unique_clients || 0}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  mt: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Rendement/cde : <strong>{kpis.revenue_per_order || 0}€</strong>
              </Typography>
            </Box>
          </Grid>
          <Grid item md={0.1} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 35, borderColor: '#e2e8f0' }}
            />
          </Grid>
          {/* 6. CA du Meilleur Intervenant */}
          <Grid item xs={12} sm={4} md={1.6}>
            <Box sx={{ px: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Leader Revenu
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#0369a1',
                  mt: 0.5,
                  letterSpacing: '-0.5px',
                }}
              >
                {kpis.top_intervenant_revenue || 0} €
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  mt: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Part CA :{' '}
                <strong>
                  {(
                    (kpis.top_intervenant_revenue / (kpis.revenue || 1)) *
                    100
                  ).toFixed(0)}
                  %
                </strong>
              </Typography>
            </Box>
          </Grid>
          <Grid item md={0.1} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ height: 35, borderColor: '#e2e8f0' }}
            />
          </Grid>
          {/* 7. Commandes du Meilleur Intervenant */}
          <Grid item xs={12} sm={4} md={1.6}>
            <Box sx={{ px: 2 }}>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Leader Volumes
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: '#7c3aed',
                  mt: 0.5,
                  letterSpacing: '-0.5px',
                }}
              >
                {kpis.top_intervenant_orders || 0}{' '}
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#64748b',
                    fontWeight: 400,
                  }}
                >
                  cdes
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: '#64748b',
                  mt: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Part cdes :{' '}
                <strong>
                  {(
                    (kpis.top_intervenant_orders / (kpis.orders || 1)) *
                    100
                  ).toFixed(0)}
                  %
                </strong>
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Ligne 1 : Les Graphiques principaux (CA et Ventes) */}
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Évolution CA
            </Typography>
            <RevenueChart data={data.revenueChart || []} />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Volume Ventes
            </Typography>
            <OrdersChart data={data.ordersChart || []} />
          </Paper>
        </Grid>

        {/* Ligne 2 : Classement, Remplissage Ateliers et Remplissage Offres */}
        <Grid item xs={12} lg={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: 4,
              height: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
              Classement Performance Intervenants
            </Typography>
            {/* Appel propre du composant avec le tableau topIntervenants */}
            <PerformanceScoreCard data={data.topIntervenants || []} />
          </Paper>
        </Grid>

        {data.platform_stats && (
          <Box sx={{ width: '100%', mt: 3 }}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                bgcolor: '#ffffff',
                border: '1px solid #f1f5f9',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                width: '100%',
              }}
            >
              <Box
                sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}
              >
                <TrendingUp sx={{ color: '#1e3a8a', fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Intelligence Analytique Globale
                </Typography>
              </Box>

              <PlatformIntelligenceDashboard
                platformData={data.platform_stats}
              />
            </Paper>
          </Box>
        )}
        <Grid item xs={12} lg={12}>
          {' '}
          {/* lg={12} force le bloc à occuper les 12 colonnes de la grille */}
          <Paper sx={{ p: 3, borderRadius: 4, height: '100%' }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Remplissage Ateliers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Statistiques et taux d'occupation en temps réel
              </Typography>
            </Box>
            <AteliersRemplissage data={data.workshopsAnalytics || []} />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 4, height: '100%' }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Remplissage Consultations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Statistiques et taux d'occupation en temps réel
              </Typography>
            </Box>
            <OffresRemplissage data={toutesLesOffres} />
          </Paper>
        </Grid>

        {/* Ligne 3 : Tableau détaillé */}

        <Grid item xs={12} lg={4}>
          <RankedClients data={data.kpis.rankedClients || []} />
        </Grid>
        {/* <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 4 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Performance Intervenants (Détails)
            </Typography>
            <IntervenantsTable data={data.topIntervenants || []} />
          </Paper>
        </Grid> */}
      </Grid>
    </Box>
  );
}
function KPICard({ title, value, subtitle, icon, color, gradient }) {
  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="caption"
          sx={{
            color: '#6b7280',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="h5"
          fontWeight={800}
          sx={{ color: '#111827', lineHeight: 1.2 }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            sx={{ color: '#9ca3af', fontWeight: 500, mt: 0.5 }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      <Avatar
        sx={{
          background: gradient,
          color: color,
          width: 48,
          height: 48,
          borderRadius: 3,
        }}
      >
        {icon}
      </Avatar>
    </Paper>
  );
}
